import React, { useState } from 'react';
import {
  Box,
  Card,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  MenuItem,
} from '@mui/material';
import {
  Add,
  Search,
  Edit,
  Delete,
  AttachMoney,
  TrendingUp,
} from '@mui/icons-material';
import { useCrudPage } from '../../hooks/useCrudPage';
import LoadingState from '../../components/common/LoadingState';
import ErrorAlert from '../../components/common/ErrorAlert';
import EmptyState from '../../components/common/EmptyState';
import SuccessSnackbar from '../../components/common/SuccessSnackbar';
import { api } from '../../services/api';

interface Deal {
  id: string;
  title: string;
  company: string;
  value: number;
  stage: 'lead' | 'qualified' | 'proposal' | 'negotiation' | 'closed_won' | 'closed_lost';
  probability: number;
  closeDate: string;
  contact: string;
}

const stages = [
  { value: 'lead', label: 'Lead', color: 'default' },
  { value: 'qualified', label: 'Qualified', color: 'info' },
  { value: 'proposal', label: 'Proposal', color: 'primary' },
  { value: 'negotiation', label: 'Negotiation', color: 'warning' },
  { value: 'closed_won', label: 'Closed Won', color: 'success' },
  { value: 'closed_lost', label: 'Closed Lost', color: 'error' },
];

const DealsPage: React.FC = () => {
  const {
    items: deals,
    loading,
    error,
    success,
    fetchItems,
    createItem,
    updateItem,
    deleteItem,
    clearSuccess,
  } = useCrudPage<Deal>({
    listFn: api.crm.deals.list,
    createFn: api.crm.deals.create,
    updateFn: api.crm.deals.update,
    deleteFn: api.crm.deals.delete,
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingDeal, setEditingDeal] = useState<Deal | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    company: '',
    value: 0,
    stage: 'lead' as Deal['stage'],
    probability: 10,
    closeDate: '',
    contact: '',
  });

  const handleOpenDialog = (deal?: Deal) => {
    if (deal) {
      setEditingDeal(deal);
      setFormData(deal);
    } else {
      setEditingDeal(null);
      setFormData({
        title: '',
        company: '',
        value: 0,
        stage: 'lead',
        probability: 10,
        closeDate: '',
        contact: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingDeal(null);
  };

  const handleSubmit = async () => {
    try {
      if (editingDeal) {
        await updateItem(editingDeal.id, formData);
      } else {
        await createItem(formData);
      }
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving deal:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this deal?')) {
      await deleteItem(id);
    }
  };

  const filteredDeals = deals.filter(
    (deal) =>
      deal.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      deal.company.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalValue = filteredDeals.reduce((sum, deal) => sum + deal.value, 0);
  const avgProbability =
    filteredDeals.length > 0
      ? filteredDeals.reduce((sum, deal) => sum + deal.probability, 0) / filteredDeals.length
      : 0;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          CRM Deals
        </Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()}>
          Add Deal
        </Button>
      </Box>

      {error && <ErrorAlert error={error} onRetry={fetchItems} />}

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Total Deals
            </Typography>
            <Typography variant="h4">{filteredDeals.length}</Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Total Value
            </Typography>
            <Typography variant="h4">${totalValue.toLocaleString()}</Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Avg Probability
            </Typography>
            <Typography variant="h4">{avgProbability.toFixed(0)}%</Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Won Deals
            </Typography>
            <Typography variant="h4">
              {filteredDeals.filter((d) => d.stage === 'closed_won').length}
            </Typography>
          </Card>
        </Grid>
      </Grid>

      <Card>
        <Box sx={{ p: 2 }}>
          <TextField
            fullWidth
            placeholder="Search deals..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {loading ? (
          <LoadingState message="Loading deals..." />
        ) : filteredDeals.length === 0 ? (
          <EmptyState
            title="No deals found"
            description="Start tracking your sales pipeline"
            actionLabel="Add Deal"
            onAction={() => handleOpenDialog()}
            icon={<TrendingUp sx={{ fontSize: 64, color: 'text.disabled' }} />}
          />
        ) : (
          <Grid container spacing={2} sx={{ p: 2 }}>
            {filteredDeals.map((deal) => {
              const stageInfo = stages.find((s) => s.value === deal.stage);
              return (
                <Grid item xs={12} sm={6} md={4} key={deal.id}>
                  <Card variant="outlined" sx={{ p: 2, height: '100%' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="h6" sx={{ fontSize: '1rem' }}>
                        {deal.title}
                      </Typography>
                      <Box>
                        <IconButton size="small" onClick={() => handleOpenDialog(deal)}>
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDelete(deal.id)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>

                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {deal.company}
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <AttachMoney fontSize="small" color="success" />
                      <Typography variant="h6">${deal.value.toLocaleString()}</Typography>
                    </Box>

                    <Chip
                      label={stageInfo?.label}
                      size="small"
                      color={stageInfo?.color as any}
                      sx={{ mb: 1 }}
                    />

                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Probability: {deal.probability}%
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={deal.probability}
                        sx={{ mt: 0.5 }}
                      />
                    </Box>

                    <Typography variant="caption" color="text.secondary">
                      Close Date: {deal.closeDate}
                    </Typography>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingDeal ? 'Edit Deal' : 'Add New Deal'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Deal Title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Company"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Value ($)"
                type="number"
                value={formData.value}
                onChange={(e) => setFormData({ ...formData, value: Number(e.target.value) })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Stage"
                value={formData.stage}
                onChange={(e) => setFormData({ ...formData, stage: e.target.value as Deal['stage'] })}
              >
                {stages.map((stage) => (
                  <MenuItem key={stage.value} value={stage.value}>
                    {stage.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Probability (%)"
                type="number"
                value={formData.probability}
                onChange={(e) =>
                  setFormData({ ...formData, probability: Number(e.target.value) })
                }
                inputProps={{ min: 0, max: 100 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Close Date"
                type="date"
                value={formData.closeDate}
                onChange={(e) => setFormData({ ...formData, closeDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Contact Person"
                value={formData.contact}
                onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit}>
            {editingDeal ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <SuccessSnackbar open={!!success} message={success || ''} onClose={clearSuccess} />
    </Box>
  );
};

export default DealsPage;
